import { toFullName } from '@repo/toolkit/to-full-name'
import Checkbox from '@repo/ui/checkbox'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import TextInput from '../../../../../../packages/ui/components/atoms/input/text-input'
import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getOls } from '../../../api/ols/get-ols'
import { getOptions } from '../../../api/options/get-options'
import { getPeriods } from '../../../api/periods/get-periods'
import { getTrips } from '../../../api/trips/get-trips'
import { getUsers } from '../../../api/user/get-users'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { CenterFilter } from '../../../lib/types/models/center/center-filter'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { getLocalizedLabel } from '../../../utils/functions/get-localized-label'
import { getOlName } from '../../../views/ols/center/utils/functions'
import { yearsFilterOptions } from '../../../views/pcr-personal-space/utils'
import CountryFlag from '../../elements/country-flag'

interface Props {
  mode: 'center' | 'ol' | 'admin'
  filter?: CenterFilter
  onFilterChange: (filter: CenterFilter) => void
}

export function CentersFilter({ filter, mode, onFilterChange }: Props) {
  function setFilter<K extends keyof CenterFilter>(key: K, value: CenterFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }
  const { t, currentLanguage } = useTranslate()

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: options } = useQuery({
    queryKey: ['options', { version: 'LIGHT' }],
    queryFn: () => getOptions({ version: 'LIGHT' }),
    enabled: mode !== 'ol'
  })

  const { data: ols } = useQuery({
    queryKey: ['ols'],
    queryFn: () => getOls(),
    enabled: mode !== 'ol'
  })

  const { data: admins } = useQuery({
    queryKey: ['admins'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } }),
    enabled: mode === 'center'
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips(),
    enabled: mode === 'center'
  })

  if (!filter) return null

  return (
    <div className={'flex flex-col gap-3'}>
      <div />
      {mode === 'center' && (
        <div className="flex flex-row gap-5">
          <RadioInput
            label="Tous"
            checked={filter?.locked === undefined}
            onCheck={() => setFilter('locked', undefined)}
          />

          <RadioInput
            label="Verrouillé"
            checked={filter?.locked === true}
            onCheck={() => setFilter('locked', true)}
          />

          <RadioInput
            label={'Non verrouillé'}
            checked={filter?.locked === false}
            onCheck={() => setFilter('locked', false)}
          />
        </div>
      )}

      {mode === 'center' && (
        <TextInput
          type="text"
          label={'NOM du centre'}
          value={filter.name}
          onChange={(e) => setFilter('name', e.target.value)}
        />
      )}

      <SelectInput
        label={t('common.year')}
        options={yearsFilterOptions}
        selectedOption={
          filter?.year
            ? {
                label: yearsFilterOptions.find((y) => y.value === filter.year)?.label ?? '',
                value: filter.year
              }
            : undefined
        }
        onChange={(year) => setFilter('year', year)}
      />

      <MultiSelectInput
        label={t('center.filters.country')}
        variant="primary"
        searchable
        options={countries?.data.map(({ name, id }) => ({
          label: name,
          value: id,
          custom: <CountryFlag possibleCountry={name} />
        }))}
        selectedOptions={filter.countryIds?.map((countryId) => ({
          label: countries?.data.find((c) => c.id === countryId)?.name ?? '',
          value: countryId
        }))}
        onChange={(countryIds) => setFilter('countryIds', countryIds)}
      />

      <MultiSelectInput
        searchable
        label={t('center.filters.periods')}
        variant={'warning'}
        options={periods?.data.map((period) => ({
          label: getLocalizedLabel(period, currentLanguage, {
            fr: 'name',
            en: 'nameEn'
          }),
          value: period.name
        }))}
        selectedOptions={filter.periods?.map((period) => {
          const selectedPeriod = periods?.data.find((p) => p.name === period)
          return {
            label: selectedPeriod
              ? getLocalizedLabel(selectedPeriod, currentLanguage, {
                  fr: 'name',
                  en: 'nameEn'
                })
              : period,
            value: period
          }
        })}
        onChange={(periods) => setFilter('periods', periods)}
      />

      {mode === 'center' && (
        <div className="flex space-x-4 justify-between">
          <DateStringInput
            label="Date d'aller"
            value={filter?.departureDate}
            mode="over"
            onChange={(date) => onFilterChange({ ...filter, departureDate: date })}
          />
          <DateStringInput
            label="Date de retour"
            value={filter?.arrivalDate}
            mode="over"
            onChange={(date) => onFilterChange({ ...filter, arrivalDate: date })}
          />
        </div>
      )}

      {mode === 'admin' && (
        <div className="flex space-x-4 justify-between">
          <DateStringInput
            label="Dates de départ après"
            value={filter?.departureDateAfter}
            mode="over"
            onChange={(date) => onFilterChange({ ...filter, departureDateAfter: date })}
          />
          <DateStringInput
            label="Dates de départ avant"
            value={filter?.departureDateBefore}
            mode="over"
            onChange={(date) => onFilterChange({ ...filter, departureDateBefore: date })}
          />
        </div>
      )}

      <MultiSelectInput
        searchable
        label={t('center.filters.formula')}
        variant={'primary'}
        options={formulas?.data?.map((formula) => ({
          label: getTranslatedName(formula, currentLanguage) || '—',
          value: getTranslatedName(formula)
        }))}
        selectedOptions={filter?.formulas?.map((formula) => {
          const selectedFormula = formulas?.data.find((f) => getTranslatedName(f) === formula)
          if (!selectedFormula) return { label: formula, value: formula }
          return {
            label: getTranslatedName(selectedFormula, currentLanguage) || '—',
            value: formula
          }
        })}
        onChange={(formulas) => onFilterChange({ ...filter, formulas })}
      />

      {(mode === 'admin' || mode === 'center') && (
        <>
          <MultiSelectInput
            searchable
            label={'Thème(s)'}
            variant={'primary'}
            options={
              options?.data.map((option) => ({
                label: getTranslatedName(option),
                value: getTranslatedName(option)
              })) ?? []
            }
            selectedOptions={filter?.mainOptions?.map((item) => ({
              label: getTranslatedName(
                options?.data.find((option) => getTranslatedName(option) === item)
              ),
              value: item
            }))}
            onChange={(mainOptions) => onFilterChange({ ...filter, mainOptions })}
          />
          <MultiSelectInput
            searchable
            label={'Option(s)'}
            variant={'primary'}
            options={
              options?.data.map((option) => ({
                label: getTranslatedName(option),
                value: getTranslatedName(option)
              })) ?? []
            }
            selectedOptions={filter?.options?.map((item) => ({
              label: getTranslatedName(
                options?.data.find((option) => getTranslatedName(option) === item)
              ),
              value: item
            }))}
            onChange={(options) => onFilterChange({ ...filter, options })}
          />
        </>
      )}

      {mode === 'admin' && (
        <MultiSelectInput
          label="Types d’hébergement"
          variant="primary"
          options={
            Object.values(AccomodationEnum).map((accomodation) => ({
              label: accomodationEnumMapping[accomodation],
              value: accomodation
            })) ?? []
          }
          selectedOptions={filter?.accomodations?.map((accomodation) => ({
            label: accomodationEnumMapping[accomodation],
            value: accomodation
          }))}
          onChange={(accomodations) => onFilterChange({ ...filter, accomodations })}
        />
      )}

      {mode === 'admin' && (
        <div className="flex flex-col gap-2">
          <Checkbox
            label="Infos voyage à diffuser"
            checked={filter?.tripBookingToDiffuse === true}
            onChange={(checked) => setFilter('tripBookingToDiffuse', checked || undefined)}
          />
          <Checkbox
            label="Jeunes à diffuser au presta"
            checked={filter?.enrollmentsToDiffuse === true}
            onChange={(checked) => setFilter('enrollmentsToDiffuse', checked || undefined)}
          />
        </div>
      )}

      {(mode === 'admin' || mode === 'center') && (
        <MultiSelectInput
          label={'Prestataire étranger'}
          variant={'primary'}
          options={ols?.data.map((ol) => ({ label: getOlName(ol), value: ol.id })) ?? []}
          selectedOptions={filter?.olIds?.map((item) => ({
            label: getOlName(ols?.data.find((ol) => ol.id === item)),
            value: item
          }))}
          onChange={(olIds) => onFilterChange({ ...filter, olIds })}
        />
      )}

      {mode === 'center' && (
        <SelectInput
          label={'Voyage principal'}
          options={
            trips?.data.map((trip) => ({
              label: TripProperties.getTripLabel(trip),
              value: trip.id
            })) ?? []
          }
          selectedOption={getSelectedOption(
            filter.mainTripId,
            TripProperties.getTripLabel(trips?.data.find((trip) => trip.id === filter.mainTripId))
          )}
          onChange={(mainTripId) => onFilterChange({ ...filter, mainTripId })}
        />
      )}

      {mode === 'center' && (
        <DateStringInput
          label="Date de RAD"
          mode="over"
          value={filter?.radDate}
          onChange={(date) => onFilterChange({ ...filter, radDate: date })}
        />
      )}

      {mode === 'center' && (
        <SelectInput
          label={'Responsable secteur'}
          options={
            admins?.data.map((admin) => ({
              label: toFullName(admin.firstName, admin.lastName),
              value: admin.id
            })) ?? []
          }
          selectedOption={getSelectedOption(
            filter.lecResponsableId,
            toFullName(
              admins?.data.find((admin) => admin.id === filter.lecResponsableId)?.firstName,
              admins?.data.find((admin) => admin.id === filter.lecResponsableId)?.lastName
            )
          )}
          onChange={(lecResponsableId) => onFilterChange({ ...filter, lecResponsableId })}
        />
      )}
    </div>
  )
}
